import React, { useState } from 'react'
import {
    useIonAlert,
	IonButton,
    IonIcon,
	IonLabel,
} from '@ionic/react'
import { Purchases } from '@revenuecat/purchases-capacitor'
import { Capacitor } from '@capacitor/core'
import { logEvent } from './firebase'
import NativePurchasing from './NativePurchasing'
import { WebPurchasing, WebSubscriptionManagement } from './WebPurchasing'
import ModalWithCancel from './ModalWithCancel'
import {
	customerHasProHistory,
	customerPlanEnd,
	shouldOfferFreeTrial,
    useClub,
    useClubHasPro,
	useSetStoreCustomerInfo,
	useStoreCustomerInfo,
	useSetShowLogin,
	useStoreCustomerInfoError,
	useUser,
    useUserHasPro,
    useUserHasProPersonally,
} from './data'
import { calendar  } from 'ionicons/icons'
import { players } from './media/teamIcon'
import './plan.css'
import GameInsightIcon from './GameInsightIcon'
import { MoreInfoLink } from './MoreInfoLink'
import { useRefreshStoreCustomerInfo } from './useRefreshStoreCustomerInfo'

//
// Current Pricing - should be reflected in Stripe (web), Apple Store Connect (ios) & Google Play.
// Also make sure any price update is reflected in Help.tsx > HelpClubsText
//
// Using Australian dollars:
//
// Basic - free, but only 1 season per user, no advanced stats
//
// Pro 	- $ 6.99/month				(USD  4.99)
//     	- $59.99/year				(USD 40.99)
//
// Club Basic - free with usual limitations - up to 40 coaches + admin tools
//
// Club Pro
// 		- $ 59.99/mo				(USD  40.99)
// 		- $599.99/year				(USD 399.99)
// 		- $299.99/6 months			(USD 199.99) - Android only, because Google Play refuses annual pricing as being too high
// 		- multiple teams/seasons + extra stats - up to 40 coaches
//
// From January 2026, it's 50% OFF FOR CLUBS -- since basically no club is using it
// 		- $ 29.99/mo				(USD  19.99)
// 		- $299.00/year				(USD 199.99)
//
//
// Club flow:
// - Admin purchases club account
// - Admin sends out invitation links
// - User clicks link, gets added as 'coach' role user to 'clubs'
// - Admin can remove users, change role to 'admin' (who can manage other users)
// - Admin can upload logo
// - Admin & Supervisor can view all teams of users, seasons, stats, etc - coaches can't view other coaches
// - Note users could be Club + Pro or Club + Basic
// - Existing Pro users - prompted to cancel
// - Existing users if Club subscription lapses - prompted to upgrade
//

export const DisplayPlan = () => {

	const user = useUser()
    const club = useClub()

    const userHasPro = useUserHasPro()
    const userHasProPersonally = useUserHasProPersonally()
    const clubHasPro = useClubHasPro()
	const userStoreCustomerInfo = useStoreCustomerInfo(user?.uid || '')
	const clubStoreCustomerInfo = useStoreCustomerInfo(club?.id || '')
	const planStoreCustomerInfo = userHasProPersonally ? userStoreCustomerInfo : clubStoreCustomerInfo
	const isTrialPlan = userHasPro && customerHasActiveTrial(planStoreCustomerInfo)
	const personalPlanEnd = userHasProPersonally ? customerPlanEnd(userStoreCustomerInfo) : null

    let warning
    if (userHasProPersonally && clubHasPro) {
        warning = <DuplicatePlanWarning />
    }

    return (
        <>
            <div className="display-user-block">
                <IonLabel>
                    Plan
                </IonLabel>
                {
					userHasProPersonally && <SubscriptionManagement isClub={false} />
                }
                <IonLabel>
                    {
                        userHasPro ? (
							<div className="profile-user-has-pro">
								<div>
									<PlanName isClub={!userHasProPersonally && !!club} name={userHasPro ? 'Pro' : 'Basic'} />
									{isTrialPlan && ' (Trial)'}
								</div>
								{
									personalPlanEnd && (
										<div className="plan-renewal-date">
											{personalPlanEnd.renews ? 'Renews' : 'Expires'} {personalPlanEnd.date.toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' })}
										</div>
									)
								}
							</div>
                        ) : 'None'
                    }
                </IonLabel>
            </div>
            {warning}
        </>
    )
}

export const DuplicatePlanWarning = () => (
    <div className="warning">
        ⚠️   You are personally subscribed to <PlanName />,
        but also receive these benefits from your club. You may wish to
        cancel your personal subscription.
    </div>
)

const customerHasActiveTrial = (info: StoreCustomerInfo | null) => {
	if (!info?.subscriptions) {
		return false
	}

	const now = new Date()

	return Object.values(info.subscriptions).some(subscription =>
		subscription.period_type === 'trial' &&
		new Date(subscription.expires_date) > now
	)
}

type DisplayUpgradeOfferProps = {
	isClub?: boolean
    noHelpLink?: boolean
}

export const DisplayUpgradeOffer = (props: DisplayUpgradeOfferProps) => {

	const { isClub } = props

	const user = useUser()
    const userHasPro = useUserHasPro()
	const clubHasPro = useClubHasPro()
	const setShowLogin = useSetShowLogin()
	const storeCustomerInfo = useStoreCustomerInfo(user?.uid || '')
	const storeCustomerInfoError = useStoreCustomerInfoError(user?.uid || '')
	const hasProHistory = customerHasProHistory(storeCustomerInfo)
	const offerFreeTrial = shouldOfferFreeTrial(storeCustomerInfo, storeCustomerInfoError)

    const [ amPurchasing, setAmPurchasing ] = useState(false)

    if ((isClub && clubHasPro) || (!isClub && userHasPro))
        return null

	const isNative = Capacitor.isNativePlatform()

	const onClick = () => {
		if (!user) {
			setShowLogin('signIn')
			return
		}

		setAmPurchasing(true)
		logEvent('considerPurchase')
	}

	let content
	if (isClub) {
		content = (
			<>
				<IonButton
					onClick={onClick}
					fill="outline"
					className="club-upgrade-offer"
				>
					Get
					<PlanName isClub={true} />
					{' '}
					🌟 50% off!
				</IonButton>
			</>
		)
	} else {
		content = (
			<div
				className="upgrade-offer"
				onClick={onClick}
			>
				<h2>
					<PlanName />
				</h2>
				<div className="selling-points">
					<div className="selling-point">
						<IonIcon icon={players} />
						<p>
							Multiple teams
						</p>
					</div>
					<div className="selling-point">
						<IonIcon icon={calendar} />
						<p>
							Multiple seasons
						</p>
					</div>
					<div className="selling-point">
                        <GameInsightIcon />
						<p>
							Flow Maps + extra stats
						</p>
					</div>
				</div>
				<p className="upgrade-offer-call-to-action">
					{offerFreeTrial ? 'Free Trial' : hasProHistory ? 'Resubscribe' : 'Subscribe'}
				</p>
                {
                    props.noHelpLink ? null : (
                        <div onClick={event => event.stopPropagation()}>
                            <MoreInfoLink helpSection="autorosterpro" text="Learn more..." />
                        </div>
                    )
                }
			</div>
		)
	}

	return (
		<>
			{content}
			<ModalWithCancel
				show={amPurchasing}
				setShow={setAmPurchasing}
			>
				{
					isNative ? (
						<NativePurchasing
							isClub={isClub}
						/>
					) : (
						<WebPurchasing
							isClub={isClub}
						/>
					)
				}
			</ModalWithCancel>
		</>
	)
}

export const RestorePurchases = () => {

	const user = useUser()
	const setStoreCustomerInfo = useSetStoreCustomerInfo(user?.uid || '')
	const refreshStoreCustomerInfo = useRefreshStoreCustomerInfo(user?.uid || '')

	const onClick = async () => {

		console.log('restorePurchases()')

		if (Capacitor.isNativePlatform()) {
			try {
				const { customerInfo } = await Purchases.restorePurchases()
				console.log("Restored purchases? TODO", customerInfo)
				if (customerInfo.entitlements.active.pro) {
					const convertedPurchaserInfo: StoreCustomerInfo = {
						...customerInfo,
						first_seen: customerInfo.firstSeen,
						last_seen: customerInfo.requestDate,
						original_app_user_id: customerInfo.originalAppUserId,
						management_url: customerInfo.managementURL,
						original_application_version: customerInfo.originalApplicationVersion,
						original_purchase_date: customerInfo.originalPurchaseDate,
						active_subscriptions: customerInfo.activeSubscriptions,
						non_subscriptions: customerInfo.nonSubscriptionTransactions,
						entitlements: {
							pro: {
								expires_date: customerInfo.entitlements.active.pro.expirationDate,
								product_identifier: customerInfo.entitlements.active.pro.identifier,
								purchase_date: customerInfo.entitlements.active.pro.latestPurchaseDate,
							}
						},
					}
					setStoreCustomerInfo(convertedPurchaserInfo)
				}
				void refreshStoreCustomerInfo()
			} catch (error) {
				console.error("Error restoring purchases", error)
			}
		} else {
			// Reload app to force recheck with RevenueCat
			window.location.reload()
		}
	}

	return (
		<IonButton
			className="restore-purchases-button"
			fill="clear"
			onClick={onClick}
		>
			Restore Purchase
		</IonButton>
	)
}

type SubscriptionManagementProps = {
	isClub: boolean
}

//
// We need to give the user some management options if:
// (a) they're on the web - either provide a link to Stripe or tell them how to cancel
// (b) they're on a device, but they subscribed via Stripe
//
export const SubscriptionManagement = (props: SubscriptionManagementProps) => {

	const { isClub } = props

	const user = useUser()
	const club = useClub()

	const [ presentAlert ] = useIonAlert()

	const id = isClub ? club?.id : user?.uid
	const info = useStoreCustomerInfo(id || '')

	console.log('SubscriptionManagement for id', id, 'is', info)

	const [ subscription ] = Object.values(info?.subscriptions || [{ }])

	if (!subscription) {
		return null
	}

	const isNative = Capacitor.isNativePlatform()

	let onClick

	if (isNative) {
		const storeText = {
			'stripe': 			"Your subscription was acquired directly from the AutoRoster.io website - please login there to manage your subscription.",
			'promotional': 		"You are currently receiving a free promotional subscription.",
			'apple': 			"View & modify your subscription in your device Settings → Your Apple ID → Subscriptions",
			'app_store':  		"View & modify your subscription in your device Settings → Your Apple ID → Subscriptions",
			'mac_app_store': 	"View & modify your subscription in your Apple account subscriptions.",
			'play_store': 		"View & modify your subscription in Google Play → Payments & subscriptions → Subscriptions.",
			'amazon': 			"View & modify your subscription in your Amazon Appstore subscriptions.",
		} as const

		const message = storeText[subscription.store as keyof typeof storeText] || 'Unknown subscription store'
		if (message) {
			onClick = () => {
				presentAlert({
					header: 'Your Subscription',
					message,
					buttons: [ 'OK' ],
				})
			}
		} else {
			console.log('Unknown subscription store!', subscription.store)
		}

	} else {
		// Web options
		if (subscription.store === 'stripe') {
			return <WebSubscriptionManagement />
		}

		const storeText = {
			'apple': 			"Your subscription was acquired via Apple. You can manage it on your Apple device in Settings → Your Apple ID → Subscriptions.",
			'app_store': 		"Your subscription was acquired via Apple. You can manage it on your Apple device in Settings → Your Apple ID → Subscriptions.",
			'mac_app_store': 	"Your subscription was acquired via Apple. You can manage it in your Apple account subscriptions.",
			'play_store': 		"Your subscription was acquired via Google Play. You can manage it on your Android device or by logging into the Google Play Store → Subscriptions.",

			'promotional': 		"You are currently receiving a free promotional subscription.",
		} as const

		const message = storeText[subscription.store as keyof typeof storeText] || `Your subscription was acquired via '${subscription.store}'. Please manage it in that store's subscription settings.`

		if (message) {
			onClick = () => {
				presentAlert({
					header: 'Your Subscription',
					message,
					buttons: [ 'OK' ],
				})
			}
		}
	}

	if (!onClick)
		return null

	return (
		<IonLabel>
			<IonButton
				fill="clear"
				onClick={onClick}
			>
				Manage...
			</IonButton>
		</IonLabel>
	)
}

type PlanNameProps = {
	isClub?: boolean
	name?: string
}

export const PlanName = (props: PlanNameProps) => {

    const planName = props.name || 'Pro'

	const prefix = props.isClub ? 'Club - ' : null

    return (
        <span className="plan-name">
            {prefix}<span className="offer-appname">AutoRoster</span> <span className="offer-planname">{planName}</span>
        </span>
    )
}
